import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AddLeadModal from './AddLeadModal';
import ClientCard from './ClientCard';
import StatsCards from './StatsCards';
import { clientService } from '../../services/clientService';

const { FiPlus, FiFilter, FiSearch, FiLoader } = FiIcons;

const Dashboard = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        if (user?.id) {
          const userClients = await clientService.getUserClients(user.id);
          setClients(userClients);
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Impossibile caricare i clienti. Riprova più tardi.');
        
        // Fallback to mock data for demo
        const mockClients = [
          {
            id: 1,
            contactName: 'Marco Rossi',
            clientName: 'Rossi Marketing SRL',
            contractValue: 5000,
            status: 'nuovo_lead',
            createdAt: new Date('2024-01-15'),
            notes: 'Interessato ai nostri servizi di marketing digitale',
            commission: null
          },
          {
            id: 2,
            contactName: 'Laura Bianchi',
            clientName: 'Bianchi Consulting',
            contractValue: 8500,
            status: 'cliente_acquisito',
            createdAt: new Date('2024-01-10'),
            notes: 'Contratto firmato per campagna pubblicitaria',
            commission: null
          },
          {
            id: 3,
            contactName: 'Giuseppe Verdi',
            clientName: 'Verdi & Associates',
            contractValue: 12000,
            status: 'pagato',
            createdAt: new Date('2024-01-05'),
            notes: 'Progetto completato con successo',
            commission: 1200
          }
        ];
        setClients(mockClients);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [user?.id]);

  const handleAddLead = async (leadData) => {
    try {
      if (!user?.id) return;
      
      const newClient = await clientService.addClient({
        ...leadData,
        userId: user.id
      });
      
      const formattedClient = {
        id: newClient.id,
        contactName: newClient.contact_name,
        clientName: newClient.client_name,
        contractValue: newClient.contract_value,
        status: 'nuovo_lead',
        createdAt: new Date(newClient.created_at),
        notes: newClient.notes,
        commission: null,
        userId: user.id
      };
      
      setClients([formattedClient, ...clients]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error adding client:', err);
      alert('Errore durante l\'aggiunta del cliente. Riprova più tardi.');
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      client.clientName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const statusOptions = [
    { value: 'all', label: 'Tutti' },
    { value: 'nuovo_lead', label: 'Nuovo Lead' },
    { value: 'in_trattativa', label: 'In Trattativa' },
    { value: 'cliente_acquisito', label: 'Cliente Acquisito' },
    { value: 'pagato', label: 'Pagato' },
    { value: 'commissionato', label: 'Commissionato' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Il Tuo CashFlow</h1>
        <p className="text-gray-600">Monitora i tuoi clienti e commissioni in tempo reale</p>
      </motion.div>

      {/* Stats */}
      <StatsCards clients={clients} />

      {/* Actions Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <SafeIcon
                icon={FiSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Cerca clienti..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Filter */}
            <div className="relative">
              <SafeIcon
                icon={FiFilter}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Add Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors"
          >
            <SafeIcon icon={FiPlus} />
            <span>Aggiungi Lead</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="text-emerald-400 mb-4">
            <SafeIcon icon={FiLoader} className="text-4xl mx-auto animate-spin" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Caricamento clienti...</h3>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6"
        >
          {error}
        </motion.div>
      )}

      {/* Clients Grid */}
      {!loading && !error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <ClientCard client={client} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredClients.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="text-gray-400 mb-4">
            <SafeIcon icon={FiSearch} className="text-4xl mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun cliente trovato</h3>
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? 'Prova a modificare i filtri di ricerca'
              : 'Inizia aggiungendo il tuo primo lead'}
          </p>
        </motion.div>
      )}

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddLead}
      />
    </div>
  );
};

export default Dashboard;